import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import {
  APPLICATION_FUNDING,
  APPLICATION_FUNDING_AMOUNT,
  BANK_APPLICATION_FUNDING,
  BANK_APPLICATION_FUNDING_AMOUNT,
  DELETE_APPLICATION_FUNDING,
  METAL_STREAM_FUNDING,
  UPDATE_APPLICATION_FUNDING,
  UPDATE_APPLICATION_FUNDING_AMOUNT,
  UPDATE_BANK_APPLICATION_FUNDING,
  UPDATE_BANK_APPLICATION_FUNDING_AMOUNT,
  UPDATE_METAL_STREAM_FUNDING,
} from '../actions/funding/fundingActionConstants';
import {
  createApplicationFundingRequest,
  createBankApplicationFundingRequest,
  updateApplicationFundingRequest,
  updateBankApplicationFundingRequest,
  createMetalStreamFundingRequest,
  updateMetalStreamFundingRequest,
  createApplicationFundingAmountRequest,
  updateApplicationFundingAmountRequest,
  createBankApplicationFundingAmountRequest,
  updateBankApplicationFundingAmountRequest,
  deleteApplicationFundingRequest,
} from '../../request/fundingRequests';
import {
  applicationFundingError,
  applicationFundingSuccess,
  bankApplicationFundingError,
  bankApplicationFundingSuccess,
  updateApplicationFundingError,
  updateApplicationFundingSuccess,
  updateBankApplicationFundingError,
  updateBankApplicationFundingSuccess,
  metalStreamFundingSuccess,
  metalStreamFundingError,
  updateMetalStreamFundingSuccess,
  updateMetalStreamFundingError,
  applicationFundingAmountError,
  applicationFundingAmountSuccess,
  updateApplicationFundingAmountError,
  updateApplicationFundingAmountSuccess,
  bankApplicationFundingAmountError,
  bankApplicationFundingAmountSuccess,
  updateBankApplicationFundingAmountError,
  updateBankApplicationFundingAmountSuccess,
  deleteApplicationFundingSuccess,
  deleteApplicationFundingError,
} from '../actions/funding/fundingActions';
import {
  handleOnCompleteError,
  rejectErrorCodeHelper,
} from '../../util/helpers/registerRejectErrorCodeHelper';
import { generateSessionOrderId } from '../../util/helpers/signifyd';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { SIGNIFYD_SESSION_ID } from '../../constants/sessionStorage';
import loadSignifydDeviceFingerprintScript from '../../signifydDeviceFingerprint';

function* applicationFunding({ payload }) {
  try {
    yield call(createApplicationFundingRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
    });
    yield put(applicationFundingSuccess(payload.data));
    yield call(payload.onSuccess);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(applicationFundingError(errorMessage));

    if (payload?.onError) {
      yield call(payload.onError, {
        type: 'manual',
        message: errorMessage,
      });
    }
  }
}

function* updateApplicationFunding({ payload }) {
  try {
    const { data } = yield call(updateApplicationFundingRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
    });
    yield put(updateApplicationFundingSuccess(data));
    yield call(payload.onSuccess);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(updateApplicationFundingError(errorMessage));

    if (payload?.onError) {
      yield call(payload.onError, {
        type: 'manual',
        message: errorMessage,
      });
    }
  }
}

function* deleteApplicationFunding({ payload }) {
  try {
    yield call(deleteApplicationFundingRequest, {
      ApplicationUid: payload.ApplicationUid,
    });
    yield put(deleteApplicationFundingSuccess(payload.data));
    if (payload?.onSuccess) {
      yield call(payload.onSuccess);
    }
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(deleteApplicationFundingError(errorMessage));

    if (payload?.onError) {
      yield call(payload.onError, {
        type: 'manual',
        message: errorMessage,
      });
    }
  }
}

function* bankApplicationFunding({ payload }) {
  try {
    const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
    const data = yield call(
      createBankApplicationFundingRequest,
      {
        ApplicationUid: payload.ApplicationUid,
        data: { ...payload.data, SessionId: sessionId },
      },
      console.log('SIGNIFYD_SESSION_ID SENT TO BE:', sessionId), // eslint-disable-line
    );
    // load Signifyd script again with different order session id
    loadSignifydDeviceFingerprintScript();
    const script = document.getElementById('sig-api');
    storeInSessionStorage(SIGNIFYD_SESSION_ID, script.dataset.orderSessionId);
    // eslint-disable-next-line no-console
    console.log(
      'NEW CREATED SIGNIFYD_SESSION_ID:',
      script.dataset.orderSessionId,
    );
    if (data) {
      yield put(bankApplicationFundingSuccess(data));
      yield call(payload.onSuccess, data);
    }
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(bankApplicationFundingError(errorMessage));

    if (payload?.onError) {
      yield call(payload.onError, {
        type: 'manual',
        message: errorMessage,
      });
    }
  }
}

function* updateBankApplicationFunding({ payload }) {
  try {
    const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
    const data = yield call(
      updateBankApplicationFundingRequest,
      {
        ApplicationUid: payload.ApplicationUid,
        data: { ...payload.data, SessionId: sessionId },
      },
      console.log('SIGNIFYD_SESSION_ID SENT TO BE:', sessionId), // eslint-disable-line
    );
    // load Signifyd script again with different order session id
    loadSignifydDeviceFingerprintScript();
    const script = document.getElementById('sig-api');
    storeInSessionStorage(SIGNIFYD_SESSION_ID, script.dataset.orderSessionId);
    // eslint-disable-next-line no-console
    console.log(
      'NEW CREATED SIGNIFYD_SESSION_ID:',
      script.dataset.orderSessionId,
    );
    if (data) {
      yield put(updateBankApplicationFundingSuccess(data));
      yield call(payload.onSuccess, data);
    }
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(updateBankApplicationFundingError(errorMessage));

    if (payload?.onError) {
      yield call(payload.onError, {
        type: 'manual',
        message: errorMessage,
      });
    }
  }
}

function* applicationFundingAmount({ payload }) {
  try {
    yield call(createApplicationFundingAmountRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
    });
    yield put(applicationFundingAmountSuccess());
    yield call(payload.onSuccess);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(applicationFundingAmountError(errorMessage));

    if (payload?.onError) {
      yield call(payload.onError, {
        type: 'manual',
        message: errorMessage,
      });
    }
  }
}

function* updateApplicationFundingAmount({ payload }) {
  try {
    yield call(updateApplicationFundingAmountRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
    });
    yield put(updateApplicationFundingAmountSuccess());
    yield call(payload.onSuccess);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(updateApplicationFundingAmountError(errorMessage));

    if (payload?.onError) {
      yield call(payload.onError, {
        type: 'manual',
        message: errorMessage,
      });
    }
  }
}

function* bankApplicationFundingAmount({ payload }) {
  try {
    yield call(
      createBankApplicationFundingAmountRequest,
      payload.ApplicationUid,
      {
        ...payload.data,
      },
    );

    yield put(bankApplicationFundingAmountSuccess());
    yield call(payload.onSuccess);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(bankApplicationFundingAmountError(errorMessage));

    if (payload?.onError) {
      yield call(payload.onError, {
        type: 'manual',
        message: errorMessage,
      });
    }
  }
}

function* updateBankApplicationFundingAmount({ payload }) {
  try {
    const script = document.getElementById('sig-api');
    yield call(
      updateBankApplicationFundingAmountRequest,
      payload.ApplicationUid,
      {
        ...payload.data,
        SessionId: script.dataset.orderSessionId,
      },
    );
    yield put(updateBankApplicationFundingAmountSuccess());
    script.dataset.orderSessionId = generateSessionOrderId();
    if (payload.onSuccess) {
      yield call(payload.onSuccess);
    }
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(updateBankApplicationFundingAmountError(errorMessage));

    if (payload?.onError) {
      yield call(payload.onError, {
        type: 'manual',
        message: errorMessage,
      });
    }
  }
}

function* metalStreamFunding({ payload }) {
  try {
    yield call(createMetalStreamFundingRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
    });
    yield put(metalStreamFundingSuccess(payload.data));
    if (payload.onSuccess) {
      yield call(payload.onSuccess);
    }
  } catch (error) {
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(metalStreamFundingError(errorMessage));
    yield call(payload.onError, {
      type: 'manual',
      message: errorMessage,
    });
  }
}

function* updateMetalStreamFunding({ payload }) {
  try {
    const { data } = yield call(updateMetalStreamFundingRequest, {
      ApplicationUid: payload.ApplicationUid,
      data: payload.data,
    });
    yield put(updateMetalStreamFundingSuccess(data));
  } catch (error) {
    const errorMessage = yield call(handleOnCompleteError, error);
    yield put(updateMetalStreamFundingError(errorMessage));
  }
}

export default function* fundingSaga() {
  yield all([
    takeLatest(APPLICATION_FUNDING, applicationFunding),
    takeLatest(UPDATE_APPLICATION_FUNDING, updateApplicationFunding),
    takeLatest(DELETE_APPLICATION_FUNDING, deleteApplicationFunding),
    takeLatest(BANK_APPLICATION_FUNDING, bankApplicationFunding),
    takeLatest(UPDATE_BANK_APPLICATION_FUNDING, updateBankApplicationFunding),
    takeLatest(METAL_STREAM_FUNDING, metalStreamFunding),
    takeLatest(UPDATE_METAL_STREAM_FUNDING, updateMetalStreamFunding),
    takeLatest(APPLICATION_FUNDING_AMOUNT, applicationFundingAmount),
    takeLatest(
      UPDATE_APPLICATION_FUNDING_AMOUNT,
      updateApplicationFundingAmount,
    ),
    takeLatest(BANK_APPLICATION_FUNDING_AMOUNT, bankApplicationFundingAmount),
    takeLatest(
      UPDATE_BANK_APPLICATION_FUNDING_AMOUNT,
      updateBankApplicationFundingAmount,
    ),
  ]);
}
